import _ from '@luna/lodash';
import type { Feature } from '@luna/gatekeeper';
import { getFeatureFlagsFromApi } from '@luna/gatekeeper';

import type {
    ActiveFormFeatures,
    Features,
    FilteredFlatFeatureMap,
    FlatArrayFeature,
    FlatFeatureMap,
    FlatFeatures,
} from './types';

export function flattenFeatures(features: Features, nameSpace?: string): FlatFeatures {
    return Object.entries(features).reduce<FlatFeatures>((flatMap, [key, feature]) => {
        const currentKey = [nameSpace, key].filter(Boolean).join('.');

        if (!feature || typeof feature !== 'object') {
            return flatMap;
        }

        if (typeof feature.enabled === 'boolean') {
            flatMap[currentKey] = {
                enabled: feature.enabled,
                // eslint-disable-next-line no-underscore-dangle
                _description: feature._description,
            };
        }

        const nestedFeature = flattenFeatures(feature, currentKey);

        return {
            ...flatMap,
            ...nestedFeature,
        };
    }, {});
}

export function nestFeatures(features: FlatFeatures): Features {
    return Object.entries(features).reduce((nestedFeatures, [key, feature]) => {
        _.set(nestedFeatures, key, feature);
        return nestedFeatures;
    }, {});
}

export function mapFeaturesToFormData(
    features: FlatFeatures,
    featuresWithoutCustomOverrides: FlatFeatures,
): FlatFeatureMap {
    const apiFlags = getFeatureFlagsFromApi();
    return Object.entries(featuresWithoutCustomOverrides).reduce<FlatFeatureMap>(
        (flatMap, [name, { enabled, _description }]) => {
            const apiValue = _.get<Feature | undefined>(apiFlags, name)?.enabled;
            return {
                ...flatMap,
                [name]: {
                    description: _description,
                    isEnabled: features[name].enabled,
                    isOverridden: enabled !== features[name].enabled,
                    valueFromApi: apiValue,
                },
            };
        },
        {},
    );
}

export function getChangedFeatures(
    baseFeatures: FlatFeatureMap,
    activeFeatures: ActiveFormFeatures,
): FlatFeatures {
    return Object.entries(baseFeatures).reduce<FlatFeatures>(
        (changedFeatures, [name, { isEnabled, isOverridden }]) => {
            if (isOverridden) {
                changedFeatures[name] = { enabled: isEnabled };
            }

            if (isEnabled && !(name in activeFeatures)) {
                changedFeatures[name] = { enabled: false };
            }

            if (!isEnabled && name in activeFeatures) {
                changedFeatures[name] = { enabled: true };
            }

            return changedFeatures;
        },
        {},
    );
}

export function filterFeatures(
    features: FlatFeatureMap,
    searchTerm: string,
): FilteredFlatFeatureMap {
    return Object.entries(features).reduce<FilteredFlatFeatureMap>(
        (featureMap, [name, feature]) => {
            featureMap[name] = {
                ...feature,
                isHidden: !name.toLowerCase().includes(searchTerm.trim().toLowerCase()),
            };

            return featureMap;
        },
        {},
    );
}

function featureComparator(a: FlatArrayFeature, b: FlatArrayFeature): number {
    const priorityComparison = Number(b.isOverridden) - Number(a.isOverridden);

    if (priorityComparison) {
        return priorityComparison;
    }

    return a.name.localeCompare(b.name);
}

export function convertFeatureMapToArray(features: FilteredFlatFeatureMap): FlatArrayFeature[] {
    return Object.entries(features)
        .map(([name, feature]) => ({ name, ...feature }))
        .sort(featureComparator);
}
